import FailedSVG from '../../../components/FailedSvg';
import { formatLinkFailure } from '../flow/linkFailureReason';
import type { LinkFailure } from '../flow/types';
import {
  BACK_TO_CHECKOUT_TAB,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON
} from './result.const';
import { ResultLayout } from './ResultLayout';

type PaymentMethodErrorProps =
  | {
      mode: 'required' | 'optional';
      failure: LinkFailure;
      busy?: boolean;
      onRetry: () => void;
      onExit: () => void;
    }
  | {
      /**
       * No flow to retry: the reason plus one way out, back to the checkout when the return
       * URL names it, otherwise to the AyeFace home page.
       */
      mode: 'standalone';
      failure: LinkFailure;
      onBackToCheckout?: () => void;
      onGoHome?: () => void;
    };

export function PaymentMethodError(props: Readonly<PaymentMethodErrorProps>) {
  const { mode, failure } = props;
  return (
    <ResultLayout>
      <FailedSVG
        className="h-40 w-40"
        role="img"
        aria-label="Payment method not added"
      />

      <h1 className="mt-4 text-center text-2xl font-bold text-gray-900">
        We couldn't add your payment method
      </h1>
      <p className="mt-3 text-center text-sm font-medium text-gray-800">
        {formatLinkFailure(failure)}
      </p>
      {props.mode === 'standalone' ? (
        props.onBackToCheckout ? (
          <button
            type="button"
            onClick={props.onBackToCheckout}
            className={`mt-8 ${PRIMARY_BUTTON}`}
          >
            Back to checkout
          </button>
        ) : (
          <>
            <p className="mt-2 text-center text-sm text-gray-500">
              {BACK_TO_CHECKOUT_TAB}
            </p>
            {props.onGoHome && (
              <button
                type="button"
                onClick={props.onGoHome}
                className={`mt-8 ${SECONDARY_BUTTON}`}
              >
                Go to AyeFace home
              </button>
            )}
          </>
        )
      ) : (
        <>
          <p className="mt-2 text-center text-sm text-gray-500">
            Please try again or pay another way at checkout.
          </p>

          <button
            type="button"
            onClick={props.onRetry}
            disabled={props.busy}
            className={`mt-8 ${PRIMARY_BUTTON}`}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={props.onExit}
            disabled={props.busy}
            className={`mt-3 ${SECONDARY_BUTTON}`}
          >
            {mode === 'required' ? 'Pay another way' : 'Skip, back to checkout'}
          </button>
        </>
      )}
    </ResultLayout>
  );
}
